/**
 * Quiz API Service
 * Handles all quiz-related API calls
 */

import apiService from './api';

class QuizApiService {
  // Instructor API methods
  
  /**
   * Check if a quiz title already exists
   */
  async checkTitleExists(title) {
    return apiService.request(`/quizzes/check-title/${encodeURIComponent(title)}`);
  }

  /**
   * Create a new quiz
   */
  async createQuiz(quizData) {
    return apiService.request('/quizzes', {
      method: 'POST',
      body: JSON.stringify(quizData)
    });
  }

  /**
   * Get instructor's quizzes
   */
  async getInstructorQuizzes(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    
    const queryString = queryParams.toString();
    return apiService.request(`/quizzes${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get assigned quizzes for instructor
   */
  async getAssignedQuizzes(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const queryString = queryParams.toString();
    return apiService.request(`/quizzes/assigned${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get quiz details (instructor view)
   */
  async getQuiz(quizId) {
    return apiService.request(`/quizzes/${quizId}`);
  }

  /**
   * Update quiz basic info (title, description)
   */
  async updateQuiz(quizId, quizData) {
    return apiService.request(`/quizzes/${quizId}`, {
      method: 'PUT',
      body: JSON.stringify(quizData)
    });
  }

  /**
   * Update quiz questions
   */
  async updateQuizQuestions(quizId, questions) {
    return apiService.request(`/quizzes/${quizId}/questions`, {
      method: 'PUT',
      body: JSON.stringify({ questions })
    });
  }


  /**
   * Assign quiz to students
   */
  async assignQuiz(quizId, assignmentData) {
    return apiService.request(`/quizzes/${quizId}/assign`, {
      method: 'POST',
      body: JSON.stringify(assignmentData)
    });
  }

  /**
   * Assign quiz to course
   */
  async assignQuizToCourse(quizId, courseData) {
    return apiService.request(`/quizzes/${quizId}/assign-course`, {
      method: 'POST',
      body: JSON.stringify(courseData)
    });
  }

  /**
   * Get quiz attempts (instructor view)
   */
  async getQuizAttempts(quizId) {
    return apiService.request(`/quizzes/${quizId}/attempts`);
  }

  /**
   * Get attempt details (instructor view)
   */
  async getAttemptDetails(attemptId) {
    return apiService.request(`/quizzes/attempts/${attemptId}`);
  }

  /**
   * Override student answer (instructor only)
   */
  async overrideStudentAnswer(attemptId, questionId, answerData) {
    console.log('🌐 [API] Making override request...');
    console.log('🌐 [API] URL:', `/quizzes/attempts/${attemptId}/responses/${questionId}/override`);
    console.log('🌐 [API] Method: PUT');
    console.log('🌐 [API] Data:', answerData);
    
    try {
      const response = await apiService.request(`/quizzes/attempts/${attemptId}/responses/${questionId}/override`, {
        method: 'PUT',
        body: JSON.stringify(answerData)
      });
      
      console.log('🌐 [API] Response received:', response);
      return response;
    } catch (error) {
      console.error('🌐 [API] Request failed:', error);
      console.error('🌐 [API] Error details:', {
        message: error.message,
        status: error.status,
        response: error.response
      });
      throw error;
    }
  }

  /**
   * Get all students (for assignment purposes)
   */
  async getStudents() {
    return apiService.request('/users/students');
  }

  /**
   * Get students enrolled in a specific course. When quizId is provided each
   * student is returned with an `is_assigned` flag indicating whether they
   * already have a non-deleted assignment for that quiz in this course.
   */
  async getStudentsByCourse(courseId, quizId) {
    const qs = quizId ? `?quizId=${encodeURIComponent(quizId)}` : '';
    return apiService.request(`/users/students/course/${courseId}${qs}`);
  }

  /**
   * Get all courses (for assignment purposes)
   */
  async getCourses() {
    return apiService.request('/courses');
  }


  /**
   * Delete quiz
   */
  async deleteQuiz(quizId) {
    return apiService.request(`/quizzes/${quizId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Get assignment details for editing (instructor only)
   */
  async getAssignmentDetails(assignmentId) {
    return apiService.request(`/quizzes/assignments/${assignmentId}`);
  }

  /**
   * Get assignment details for student
   */
  async getStudentAssignmentDetails(assignmentId) {
    return apiService.request(`/quizzes/student/assignments/${assignmentId}`);
  }

  /**
   * Update assignment details
   */
  async updateAssignment(assignmentId, assignmentData) {
    return apiService.request(`/quizzes/assignments/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify(assignmentData)
    });
  }

  /**
   * Unassign quiz (delete assignment)
   */
  async unassignQuiz(assignmentId) {
    return apiService.request(`/quizzes/assignments/${assignmentId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Unassign multiple quiz assignments
   */
  async unassignMultipleQuizzes(assignmentIds) {
    // Unassign them one by one (backend currently supports single assignment)
    const results = await Promise.allSettled(
      assignmentIds.map(id => this.unassignQuiz(id))
    );
    
    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    
    return {
      success: failed === 0,
      message: failed === 0 
        ? `Successfully unassigned ${successful} assignment(s)`
        : `Unassigned ${successful} assignment(s), ${failed} failed`,
      data: {
        successful,
        failed
      }
    };
  }

  /**
   * Resume quiz attempts for specific assignments
   * Preserves all answers while allowing students to continue/resume
   */
  async resumeStudentQuizAttempts(quizId, assignmentIds) {
    return apiService.request(`/quizzes/${quizId}/resume-attempts`, {
      method: 'POST',
      body: JSON.stringify({ assignmentIds })
    });
  }

  /**
   * Grant partial retake for specific questions
   * Allows student to retake selected questions with timer options
   */
  async grantPartialRetake(quizId, assignmentId, options) {
    return apiService.request(`/quizzes/${quizId}/partial-retake`, {
      method: 'POST',
      body: JSON.stringify({
        assignmentId,
        questionNumbers: options.questionNumbers,
        timerOption: options.timerOption,
        extendMinutes: options.extendMinutes,
        timeRemaining: options.timeRemaining
      })
    });
  }

  /**
   * Reset quiz attempts for specific assignments
   */
  async resetStudentQuizAttempts(quizId, assignmentIds) {
    return apiService.request(`/quizzes/${quizId}/reset-attempts`, {
      method: 'POST',
      body: JSON.stringify({ assignmentIds })
    });
  }

  /**
   * Reset timer for quiz attempts (sets started_at to NOW())
   * This gives students the full time limit remaining
   */
  async resetQuizTimer(quizId, assignmentIds) {
    return apiService.request(`/quizzes/${quizId}/reset-timer`, {
      method: 'POST',
      body: JSON.stringify({ assignmentIds })
    });
  }

  /**
   * Extend timer for quiz attempts (adds minutes to time_limit_seconds)
   */
  async extendQuizTimer(quizId, assignmentIds, minutes) {
    return apiService.request(`/quizzes/${quizId}/extend-timer`, {
      method: 'POST',
      body: JSON.stringify({ assignmentIds, minutes })
    });
  }

  /**
   * Disable timer for quiz attempts (sets time_limit_seconds to NULL)
   */
  async disableQuizTimer(quizId, assignmentIds) {
    return apiService.request(`/quizzes/${quizId}/disable-timer`, {
      method: 'POST',
      body: JSON.stringify({ assignmentIds })
    });
  }

  // Student API methods
  
  /**
   * Get student's quiz assignments
   */
  async getStudentAssignments(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const queryString = queryParams.toString();
    return apiService.request(`/quizzes/student/assignments${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Start quiz attempt
   */
  async startAttempt(assignmentId) {
    return apiService.request(`/quizzes/student/assignments/${assignmentId}/start`, {
      method: 'POST'
    });
  }

  /**
   * Get existing quiz attempt
   */
  async getAttempt(attemptId) {
    return apiService.request(`/quizzes/student/attempts/${attemptId}`);
  }

  /**
   * Save quiz response
   */
  async saveResponse(attemptId, responseData) {
    return apiService.request(`/quizzes/student/attempts/${attemptId}/response`, {
      method: 'POST',
      skipVersionCheck: true,
      body: JSON.stringify(responseData)
    });
  }

  /**
   * Submit quiz attempt
   */
  async submitAttempt(attemptId) {
    const response = await apiService.request(`/quizzes/student/attempts/${attemptId}/submit`, {
      method: 'POST',
      skipVersionCheck: true,
      timeout: 120000
    });
    return response;
  }

  /**
   * Auto-submit quiz attempt (for single-attempt quizzes when user leaves)
   */
  async autoSubmitAttempt(attemptId) {
    return apiService.request(`/quizzes/student/attempts/${attemptId}/auto-submit`, {
      method: 'POST',
      skipVersionCheck: true,
      timeout: 120000
    });
  }

  /**
   * Get attempt result
   */
  async getAttemptResult(attemptId) {
    const response = await apiService.request(`/quizzes/student/attempts/${attemptId}/result`);
    return response;
  }

  // Utility functions for status calculation
  
  /**
   * Get assignment status based on attempt data
   */
  getAssignmentStatus(assignment) {
    // The status should come from the backend, but if not available, calculate it
    if (assignment.status) {
      // Check if it's overdue based on due date
      const dueAt = assignment.assignment_due_at || assignment.quiz_due_at;
      if (assignment.status !== 'completed' && this.isOverdue(dueAt)) {
        return 'overdue';
      }
      return assignment.status;
    }
    
    // Fallback calculation
    const dueAt = assignment.assignment_due_at || assignment.quiz_due_at;
    
    if (assignment.submitted_at) {
      // Check if submission was made after due date
      const submittedAt = new Date(assignment.submitted_at);
      const dueDate = new Date(dueAt);
      if (dueAt && submittedAt > dueDate) {
        return 'late';
      }
      return 'completed';
    } else if (assignment.started_at) {
      return this.isOverdue(dueAt) ? 'overdue' : 'in_progress';
    } else {
      return this.isOverdue(dueAt) ? 'overdue' : 'not_started';
    }
  }

  /**
   * Check if assignment is overdue
   */
  isOverdue(dueAt) {
    if (!dueAt) return false;
    
    try {
      const dueDate = new Date(dueAt);
      const now = new Date();
      return now > dueDate;
    } catch (error) {
      console.error('Error checking if overdue:', error);
      return false;
    }
  }

  /**
   * Check if assignment is due soon (within 24 hours)
   */
  isDueSoon(dueAt) {
    if (!dueAt) return false;
    
    try {
      const dueDate = new Date(dueAt);
      const now = new Date();
      const diffMs = dueDate - now;
      const diffHours = diffMs / (1000 * 60 * 60);
      
      return diffHours > 0 && diffHours <= 24;
    } catch (error) {
      console.error('Error checking if due soon:', error);
      return false;
    }
  }

  /**
   * Get server time to sync client timer
   */
  async getServerTime() {
    try {
      const response = await apiService.request('/config/server-time');
      if (response.success && response.data && response.data.serverTime) {
        return new Date(response.data.serverTime);
      }
      // Fallback to current time if endpoint not available
      return new Date();
    } catch (error) {
      console.warn('Failed to get server time, using client time:', error);
      return new Date();
    }
  }

  /**
   * Calculate time remaining for an attempt with server time sync
   */
  async calculateTimeRemaining(startedAt, timeLimitSeconds, serverTime = null) {
    if (!startedAt || !timeLimitSeconds) return null;
    
    try {
      const startTime = new Date(startedAt);
      // Use server time if provided, otherwise get it
      let now;
      if (serverTime) {
        now = new Date(serverTime);
      } else {
        // Try to get server time, but don't block if it fails
        try {
          now = await this.getServerTime();
        } catch (e) {
          // Fallback to client time
          now = new Date();
        }
      }
      
      const elapsed = Math.floor((now - startTime) / 1000);
      const remaining = timeLimitSeconds - elapsed;
      
      return Math.max(0, remaining);
    } catch (error) {
      console.error('Error calculating time remaining:', error);
      return null;
    }
  }

  /**
   * Calculate time remaining synchronously (for timer updates)
   * Uses client time but accounts for clock skew if serverTimeOffset is provided
   */
  calculateTimeRemainingSync(startedAt, timeLimitSeconds, serverTimeOffset = 0) {
    if (!startedAt || !timeLimitSeconds) return null;
    
    try {
      const startTime = new Date(startedAt);
      const now = new Date(Date.now() + serverTimeOffset); // Apply offset to account for clock skew
      const elapsed = Math.floor((now - startTime) / 1000);
      const remaining = timeLimitSeconds - elapsed;
      
      return Math.max(0, remaining);
    } catch (error) {
      console.error('Error calculating time remaining:', error);
      return null;
    }
  }

  /**
   * Format time remaining in seconds to a readable string
   */
  formatTimeRemaining(seconds) {
    if (seconds === null || seconds === undefined) return 'No time limit';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${remainingSeconds}s`;
    }
  }

  /**
   * Override quiz attempt score
   */
  async overrideAttemptScore(attemptId, score, notes) {
    return apiService.request(`/quizzes/attempts/${attemptId}/score/override`, {
      method: 'PUT',
      body: JSON.stringify({ score, notes })
    });
  }

  /**
   * Update retake/edit permissions for a quiz attempt (admin only)
   */
  async updateAttemptPermissions(attemptId, permissions) {
    return apiService.request(`/quizzes/attempts/${attemptId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify(permissions)
    });
  }

  /**
   * Get retake/edit permissions for a quiz attempt
   */
  async getAttemptPermissions(attemptId) {
    return apiService.request(`/quizzes/attempts/${attemptId}/permissions`);
  }

  /**
   * Grade unsubmitted quiz (create attempt with grade)
   */
  async gradeUnsubmittedQuiz(assignmentId, studentId, score, notes) {
    return apiService.request(`/quizzes/assignments/${assignmentId}/students/${studentId}/grade`, {
      method: 'POST',
      body: JSON.stringify({ score, notes })
    });
  }

  /**
   * Manually re-grade all attempts for a quiz
   */
  async regradeAllAttempts(quizId) {
    return apiService.request(`/quizzes/${quizId}/regrade-all`, {
      method: 'POST'
    });
  }
}

const quizApiService = new QuizApiService();
export default quizApiService;
